import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import livekitService from '../../services/livekitService';
import './CallInterface.css';

// Minimal inline SVG icons for a professional look
const IconMic = ({ muted = false }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    {muted ? (
      <>
        <path d="M12 14a4 4 0 0 0 4-4V6a4 4 0 0 0-7.23-2.47" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M6 10a6 6 0 0 0 10.9 3.6M19 10a7 7 0 0 1-7 7v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2 2l20 20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      </>
    ) : (
      <>
        <rect x="9" y="2" width="6" height="12" rx="3" stroke="currentColor" strokeWidth="1.8"/>
        <path d="M5 10a7 7 0 0 0 14 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        <path d="M12 17v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      </>
    )}
  </svg>
);

const IconVideo = ({ off = false }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    {off ? (
      <>
        <rect x="3" y="7" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.8"/>
        <path d="M21 9l-4 3v0l4 3V9z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
        <path d="M2 2l20 20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      </>
    ) : (
      <>
        <rect x="3" y="7" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.8"/>
        <path d="M21 9l-4 3v0l4 3V9z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      </>
    )}
  </svg>
);

const IconPhoneEnd = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d="M4.5 13.5c3-3 12-3 15 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M6 16l-2.5-2.5M18 16l2.5-2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const CallInterface = ({ onEndCall }) => {
  const { t } = useTranslation();
  const { 
    connectionStatus, 
    changeTab, 
    TAB_TYPES
  } = useChat();
  const { 
    disconnectFromVoice, 
    isAuthenticated, 
    voiceConnected 
  } = useAuth();
  const [isMuted, setIsMuted] = useState(true); // Microphone MUTED by default
  const [isCameraOn, setCameraOn] = useState(false); // Camera OFF by default
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [isInitializingCamera, setIsInitializingCamera] = useState(false); // Not initializing by default
  
  const userVideoRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);
  const handleEndCallRef = useRef(null);

  // Simple ActiveSpeakersChanged setup
  useEffect(() => {
    console.log('🔧 CallInterface: Setting up ActiveSpeakersChanged listener');
    
    // Set up the ActiveSpeakersChanged callback
    livekitService.setOnActiveSpeakersChanged((speakers) => {
      // Check if any speaker is AI
      const aiIdentifiers = ['assistant', 'bot', 'ai', 'pipecat'];
      const aiSpeaking = speakers.some(speaker => 
        aiIdentifiers.some(ai => speaker.identity.toLowerCase().includes(ai.toLowerCase()))
      );
      
      console.log(`🤖 AI Speaking: ${aiSpeaking}`);
      setIsAISpeaking(aiSpeaking);
    });

    // Sync mute state with LiveKit (check periodically)
    const syncStateInterval = setInterval(() => {
      const livekitMuted = livekitService.isMicrophoneMuted();
      if (livekitMuted !== isMuted) {
        console.log(`🔄 Syncing mute state: ${livekitMuted}`);
        setIsMuted(livekitMuted);
      }
    }, 1000); // Check every second

    return () => {
      // Clean up
      livekitService.setOnActiveSpeakersChanged(null);
      clearInterval(syncStateInterval);
    };
  }, [isMuted]);

  // Ensure microphone starts muted when voice connects
  useEffect(() => {
    if (!voiceConnected) return;
    (async () => {
      try {
        await livekitService.setMicrophoneMuted(true);
        setIsMuted(true);
        console.log('🔇 Ensured microphone starts muted on connect');
      } catch (error) {
        console.warn('⚠️ Could not enforce initial mute:', error);
      }
    })();
  }, [voiceConnected]);

  // Monitor camera state and send status events every 5 seconds
  useEffect(() => {
    if (!voiceConnected) return;

    // Function to send camera status event
    const sendCameraStatus = async () => {
      try {
        const cameraStatusEvent = {
          type: 'camera_status',
          data: {
            camera_on: isCameraOn
          },
          timestamp: Date.now()
        };

        await livekitService.sendData(cameraStatusEvent);
        console.log(`📹 Sent camera status: ${isCameraOn ? 'ON' : 'OFF'}`);
      } catch (error) {
        console.error('❌ Error sending camera status:', error);
      }
    };

    // Send initial status immediately
    sendCameraStatus();

    // Set up interval to send status every 5 seconds
    const statusInterval = setInterval(() => {
      sendCameraStatus();
    }, 5000); // 5 seconds

    // Cleanup interval on unmount or when voice disconnects
    return () => {
      clearInterval(statusInterval);
    };
  }, [voiceConnected, isCameraOn]); // Re-run when camera state changes or voice connection changes
  
  // Global emergency media cleanup function
  useEffect(() => {
    // Add global emergency cleanup function to window
    window.emergencyStopAllMedia = () => {
      console.log('🚨 EMERGENCY: Stopping ALL media globally...');
      
      document.querySelectorAll('video, audio').forEach((element, index) => {
        if (element.srcObject) {
          console.log(`🛑 Emergency stopping element ${index}`);
          element.srcObject.getTracks().forEach(track => track.stop());
          element.srcObject = null;
        }
      });
      
      console.log('🚨 Emergency cleanup completed');
    };
    
    return () => {
      delete window.emergencyStopAllMedia;
    };
  }, []);

  // Camera and microphone initialization has been removed as it's not used
  // and the component now only handles audio for speaking detection

  // Cleanup media streams and audio context
  useEffect(() => {
    const currentUserVideo = userVideoRef.current;
    const currentMediaStream = mediaStream;
    
    return () => {
      console.log('🧹 CallInterface: Cleaning up media resources...');
      
      // Stop all tracks in the media stream
      if (currentMediaStream) {
        currentMediaStream.getTracks().forEach(track => track.stop());
      }
      
      // Clean up video element
      if (currentUserVideo?.srcObject) {
        currentUserVideo.srcObject.getTracks().forEach(track => track.stop());
        currentUserVideo.srcObject = null;
      }
      
      // Clean up audio context
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(err => 
          console.warn('⚠️ Error closing audio context:', err)
        );
      }
      
      console.log('✅ Media resources cleaned up');
    };
  }, [mediaStream]);

  // Real-time speaking detection using audio analysis
  const detectSpeaking = () => {
    if (!analyserRef.current) return;
    
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    
    const checkAudio = () => {
      analyserRef.current.getByteFrequencyData(dataArray);
      
      // Calculate average volume
      const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
      
      // Threshold for speaking detection
      const speakingThreshold = 20;
      const speaking = average > speakingThreshold;
      setIsUserSpeaking(speaking);
      
      requestAnimationFrame(checkAudio);
    };
    
    checkAudio();
  };

  // Initialize only microphone for speaking detection (no camera)
  const initializeAudioOnly = async () => {
    try {
      console.log('🔊 Requesting microphone access only...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true
        },
        video: false
      });

      // Set up audio analysis for speaking detection
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      microphoneRef.current = microphone;

      setMediaStream(stream);
      detectSpeaking();
      console.log('✅ Microphone initialized for speaking detection');
    } catch (error) {
      console.error('❌ Error initializing microphone only:', error);
    }
  };

  // Initialize only camera for local preview (no microphone)
  const initializeCameraOnly = async () => {
    try {
      setIsInitializingCamera(true);
      setCameraError(null);
      console.log('📹 Requesting camera access only...');

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 320, max: 640 },
          height: { ideal: 240, max: 480 },
          frameRate: { ideal: 15, max: 24 }
        },
        audio: false
      });

      if (userVideoRef.current) {
        userVideoRef.current.onloadedmetadata = () => setIsInitializingCamera(false);
        userVideoRef.current.onerror = () => setIsInitializingCamera(false);
        userVideoRef.current.srcObject = stream;
        userVideoRef.current.play().catch(() => {});
      }

      setCameraOn(true);
      setIsInitializingCamera(false);
      console.log('✅ Camera initialized (no microphone)');
    } catch (error) {
      console.error('❌ Error initializing camera only:', error);
      setCameraOn(false);
      setCameraError('Failed to access camera. Please check permissions.');
      setIsInitializingCamera(false);
    }
  };

  const handleMuteToggle = async () => {
    const newMutedState = !isMuted;
    
    try {
      if (!newMutedState && !analyserRef.current) {
        // User wants to unmute and we don't have audio analysis yet
        console.log('🎤 User unmuting, initializing microphone only...');
        await initializeAudioOnly();
      }
      
      // Use LiveKit's proper mute functionality
      await livekitService.setMicrophoneMuted(newMutedState);
      setIsMuted(newMutedState);
      console.log(`🎤 Microphone ${newMutedState ? 'muted' : 'unmuted'} via LiveKit`);

      if (newMutedState) {
        // Ensure UI reflects no speaking while muted
        setIsUserSpeaking(false);
      }
    } catch (error) {
      console.error('❌ Error toggling microphone:', error);
      // Don't change the UI state if the call failed
    }
  };

  const handleCameraToggle = async () => {
    const newCameraState = !isCameraOn;
    
    if (newCameraState) {
      // User wants to turn camera ON
      console.log('📹 User enabling camera, initializing media...');
      if (!userVideoRef.current || !userVideoRef.current.srcObject) {
        // Initialize camera only to avoid affecting mic
        await initializeCameraOnly();
      } else {
        // Re-enable existing camera tracks
        setCameraOn(true);
        const videoTracks = userVideoRef.current.srcObject.getVideoTracks?.() || [];
        videoTracks.forEach(track => { track.enabled = true; });
      }
    } else {
      // User wants to turn camera OFF
      console.log('📹 User disabling camera');
      setCameraOn(false);
      // Prefer releasing the camera stream attached to the video element
      if (userVideoRef.current && userVideoRef.current.srcObject) {
        const stream = userVideoRef.current.srcObject;
        const videoTracks = stream.getVideoTracks?.() || [];
        videoTracks.forEach(track => track.stop());
        userVideoRef.current.srcObject = null;
      }
    }
  };

  const nuclearStopAllMedia = useCallback(async () => {
    console.log('💥 NUCLEAR CLEANUP: Stopping ALL media in browser...');
    
    // Method 1: Stop our tracked streams
    if (mediaStream) {
      console.log('📹 Method 1: Stopping our mediaStream...');
      mediaStream.getTracks().forEach((track, index) => {
        console.log(`🛑 Stopping our track ${index}: ${track.kind}`);
        track.stop();
      });
    }
    
    // Method 2: Stop video element streams
    if (userVideoRef.current?.srcObject) {
      console.log('📹 Method 2: Stopping video element streams...');
      userVideoRef.current.srcObject.getTracks().forEach(track => {
        console.log(`🛑 Stopping video track: ${track.kind}`);
        track.stop();
      });
      userVideoRef.current.srcObject = null;
    }
    
    // Method 3: Get all video elements and stop their streams
    console.log('🔍 Method 3: Finding all video elements...');
    const allVideos = document.querySelectorAll('video');
    allVideos.forEach((video, index) => {
      if (video.srcObject) {
        console.log(`📹 Found video ${index} with srcObject, stopping tracks...`);
        video.srcObject.getTracks().forEach(track => {
          console.log(`🛑 Stopping video element track: ${track.kind}`);
          track.stop();
        });
        video.srcObject = null;
      }
    });
    
    // Method 4: Get all audio elements and stop their streams  
    console.log('🔍 Method 4: Finding all audio elements...');
    const allAudios = document.querySelectorAll('audio');
    allAudios.forEach((audio, index) => {
      if (audio.srcObject) {
        console.log(`🔊 Found audio ${index} with srcObject, stopping tracks...`);
        audio.srcObject.getTracks().forEach(track => {
          console.log(`🛑 Stopping audio element track: ${track.kind}`);
          track.stop();
        });
        audio.srcObject = null;
      }
    });
    
    // Method 5: Nuclear option - try to stop ALL getUserMedia streams
    console.log('💣 Method 5: Nuclear getUserMedia cleanup...');
    try {
      // This is a hack - request new streams and immediately stop them
      // This often forces the browser to release existing streams
      for (let i = 0; i < 3; i++) {
        try {
          console.log(`🔥 Nuclear attempt ${i + 1}...`);
          const nuclearStream = await navigator.mediaDevices.getUserMedia({ 
            video: true, 
            audio: true 
          });
          nuclearStream.getTracks().forEach(track => {
            console.log(`💥 Nuclear stopping: ${track.kind}`);
            track.stop();
          });
        } catch (nuclearError) {
          console.log(`🚫 Nuclear attempt ${i + 1} blocked (probably good):`, nuclearError.name);
        }
        
        // Small delay between attempts
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    } catch (nuclearError) {
      console.log('💥 Nuclear cleanup error (expected):', nuclearError.name);
    }
    
    // Method 6: Close any lingering audio contexts
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      console.log('🎵 Method 6: Force closing audio context...');
      try {
        await audioContextRef.current.close();
      } catch (audioError) {
        console.warn('⚠️ Audio context close error:', audioError);
      }
    }
    
    // Clear all our references
    setMediaStream(null);
    
    console.log('💥 NUCLEAR CLEANUP COMPLETED');
    
    // Give browser a moment to actually stop the streams
    await new Promise(resolve => setTimeout(resolve, 500));
  }, [mediaStream]);

  const handleEndInterview = async () => {
    try {
      console.log('🛑 Sending manual end interview request...');

      // Send end interview event to backend via LiveKit data channel
      const endInterviewEvent = {
        type: 'end_interview_manual',
        timestamp: Date.now()
      };

      // Send the event to the backend
      await livekitService.sendData(JSON.stringify(endInterviewEvent));
      console.log('✅ Manual end interview request sent');

      // The backend will respond with interview_ended event which will trigger cleanup
    } catch (error) {
      console.error('❌ Error sending end interview request:', error);
      // Fallback: End the call directly if sending the event fails
      await handleEndCall();
    }
  };

  const handleEndCall = useCallback(async () => {
    try {
      console.log('🔚 CallInterface: Starting comprehensive end call cleanup...');
      
      // Step 1: NUCLEAR media cleanup - Stop ALL media in the browser
      await nuclearStopAllMedia();
      
      // Step 2: Close audio context
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        console.log('🎵 Closing audio context...');
        try {
          await audioContextRef.current.close();
        } catch (audioError) {
          console.warn('⚠️ Error closing audio context:', audioError);
        }
      }
      
      // Step 3: Disconnect from voice/LiveKit services
      console.log('🔊 Disconnecting from voice...');
      await disconnectFromVoice();
      
      console.log('🔌 Disconnecting from LiveKit service...');
      try {
        await livekitService.disconnect();
      } catch (livekitError) {
        console.warn('⚠️ Error disconnecting from LiveKit service:', livekitError);
      }
      
      // Step 4: Reset all component states
      setIsMuted(false);
      setCameraOn(true);
      setIsUserSpeaking(false);
      setIsAISpeaking(false);
      setMediaStream(null);
      
      // Step 5: Final verification - ensure no media devices are still active
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        console.log('📋 Final device check after cleanup:', devices.length, 'devices found');
      } catch (deviceError) {
        console.warn('⚠️ Could not enumerate devices after cleanup:', deviceError);
      }
      
      console.log('✅ CallInterface: Complete cleanup finished, calling parent end handler');
      
      // Step 6: Call the parent's end call handler which will redirect to login
      if (onEndCall) {
        await onEndCall();
      } else {
        console.warn('⚠️ No onEndCall handler provided, forcing redirect to login');
        // Fallback: Direct redirect to login if no callback
        window.location.href = '/';
      }
      
    } catch (error) {
      console.error('❌ Error ending call:', error);
      
      // Emergency fallback: Ensure cleanup happens even if errors occur
      console.log('🚨 Executing emergency cleanup...');
      
      try {
        await nuclearStopAllMedia();
        
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          await audioContextRef.current.close();
        }
      } catch (cleanupError) {
        console.error('❌ Error during emergency cleanup:', cleanupError);
      }
      
      // Always try to call parent handler, even after errors
      if (onEndCall) {
        try {
          await onEndCall();
        } catch (callbackError) {
          console.error('❌ Error calling onEndCall:', callbackError);
          // Last resort: Direct redirect
          window.location.href = '/';
        }
      } else {
        // Direct redirect as fallback
        window.location.href = '/';
      }
    }
  }, [disconnectFromVoice, onEndCall, nuclearStopAllMedia]);

  // Store handleEndCall in ref so it can be called from event listeners
  useEffect(() => {
    handleEndCallRef.current = handleEndCall;
  }, [handleEndCall]);

  // Show authentication required message if not logged in
  if (!isAuthenticated) {
    return (
      <div className="call-interface">
        <div className="auth-required">
          <div className="auth-required-content">
            <div className="auth-icon">🔒</div>
            <h2>Authentication Required</h2>
            <p>Please log in to access the Call Interface</p>
            <button 
              className="control-btn"
              onClick={() => changeTab(TAB_TYPES.GENERAL)}
            >
              <span className="control-icon">💬</span>
              <span className="control-label">Go to Chat</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show voice connection required message if not connected to voice
  if (!voiceConnected) {
    return (
      <div className="call-interface">
        <div className="auth-required">
          <div className="auth-required-content">
            <div className="auth-icon">📞</div>
            <h2>{t('call.voiceConnectionRequired')}</h2>
            <p>{t('call.connectToUse')}</p>
            <button 
              className="control-btn"
              onClick={() => changeTab(TAB_TYPES.GENERAL)}
            >
              <span className="control-icon">💬</span>
              <span className="control-label">{t('call.goToChat')}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="call-interface">
      {/* Call Status Bar */}
      <div className="call-status-bar" aria-live="polite">
        <div className="call-info">
          <span className={`status-dot ${connectionStatus}`}></span>
          <span className="call-status">
            {t(`call.status.${connectionStatus === 'connected' ? 'connected' : 'connecting'}`)}
          </span>
          <span className="call-duration">{t('call.status.live')}</span>
        </div>
        {cameraError && (
          <div className="camera-error-notification">
            <span className="error-icon">!</span>
            <span className="error-text">{cameraError}</span>
            <button 
              className="retry-camera-btn"
              onClick={initializeCameraOnly}
              disabled={isInitializingCamera}
            >
              {isInitializingCamera ? t('call.camera.connecting') + '…' : t('call.labels.tryAgain')}
            </button>
          </div>
        )}
      </div>

      {/* Video Container */}
      <div className="video-container">
        {/* User Video */}
        <div className={`video-frame user-video ${(isUserSpeaking && !isMuted) ? 'speaking' : ''}`}>
          <video
            ref={userVideoRef}
            autoPlay
            muted
            playsInline
            preload="metadata"
            className="video-element"
            style={{ 
              display: isCameraOn ? 'block' : 'none',
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          {(!isCameraOn || !mediaStream || isInitializingCamera) && (
            <div className="video-placeholder">
              <div className="avatar-placeholder user-avatar">
                <span className="avatar-icon">{t('call.labels.you').toUpperCase()}</span>
              </div>
              <div className="camera-off-overlay">
                {isInitializingCamera ? (
                  <div className="camera-loading">
                    <div className="loading-spinner"></div>
                    <div>{t('call.camera.connecting')}</div>
                    <div className="loading-progress">{t('call.camera.loading')}</div>
                  </div>
                ) : (
                  <div className="camera-off-content">
                    <div className="camera-off-message">{t('call.camera.off')}</div>
                    <div className="camera-enable-hint">
                      {t('call.camera.enableHint')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {isMuted && (
            <div className="corner-badges" title="Microphone muted">
              <span className="badge muted">{t('call.microphone.muted')}</span>
            </div>
          )}
          <div className="video-label">
            <span className="participant-name">{t('call.labels.you')}</span>
            {isUserSpeaking && !isMuted && <span className="speaking-indicator">{t('call.microphone.speaking')}</span>}
          </div>
        </div>

        {/* AI Video */}
        <div className={`video-frame ai-video ${isAISpeaking ? 'speaking' : ''}`}>
          <div className="video-placeholder">
            <div className="avatar-placeholder ai-avatar">
              <span className="avatar-icon">AI</span>
            </div>
            <div className="video-label">
              <span className="participant-name">{t('call.labels.aiAssistant')}</span>
              {isAISpeaking && <span className="speaking-indicator">{t('call.microphone.speaking')}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Call Controls */}
      <div className="call-controls">
        <div className="controls-container">
          <button 
            className={`control-btn mute-btn ${isMuted ? 'active' : ''} ${isInitializingCamera ? 'loading' : ''}`}
            onClick={handleMuteToggle}
            title={isMuted ? t('call.microphone.unmute') : t('call.microphone.mute')}
            aria-pressed={!isMuted}
            disabled={isInitializingCamera}
          >
            <span className="control-icon" aria-hidden="true">
              {isInitializingCamera ? '…' : <IconMic muted={isMuted} />}
            </span>
            <span className="control-label">
              {isMuted ? t('call.microphone.unmute') : t('call.microphone.mute')}
            </span>
          </button>

          <button 
            className={`control-btn camera-btn ${!isCameraOn ? 'active' : ''} ${isInitializingCamera ? 'loading' : ''}`}
            onClick={handleCameraToggle}
            title={isCameraOn ? t('call.camera.disable') : t('call.camera.enable')}
            aria-pressed={isCameraOn}
            disabled={isInitializingCamera}
          >
            <span className="control-icon" aria-hidden="true">
              {isInitializingCamera ? '…' : <IconVideo off={!isCameraOn} />}
            </span>
            <span className="control-label">
              {isCameraOn ? t('call.camera.on') : t('call.camera.off')}
            </span>
          </button>

          <button
            className="control-btn end-interview-btn"
            onClick={handleEndInterview}
            title="End Interview"
            style={{ background: 'rgba(255, 152, 0, 0.2)', borderColor: 'rgba(255, 152, 0, 0.5)' }}
          >
            <span className="control-icon" aria-hidden="true">⏹️</span>
            <span className="control-label">End Interview</span>
          </button>

          <button
            className="control-btn end-call-btn"
            onClick={handleEndCall}
            title={t('call.leave')}
          >
            <span className="control-icon" aria-hidden="true"><IconPhoneEnd /></span>
            <span className="control-label">{t('call.leave')}</span>
          </button>

          {/* Debug button to test AI animation */}
          {/* <button 
            className="control-btn test-btn"
            onClick={() => {
              console.log('🧪 Test: Toggling AI speaking state');
              setIsAISpeaking(!isAISpeaking);
            }}
            title="Test AI Animation"
            style={{ background: 'rgba(255, 255, 0, 0.2)' }}
          >
            <span className="control-icon">🧪</span>
            <span className="control-label">Test AI</span>
          </button> */}
        </div>
      </div>
    </div>
  );
};

export default CallInterface;